import { Button } from "#app/components/ui/button.tsx";
import { deletePhoto, getPhotos, uploadPhoto } from "#app/utils/photos.server.ts";
import { createToastHeaders } from "#app/utils/toast.server.ts";
import { ActionFunctionArgs, LoaderFunctionArgs } from "@remix-run/node";
import { json, useFetcher, useLoaderData } from "@remix-run/react";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Trash2, X } from "lucide-react";
import { getUserId } from "#app/utils/auth.server.ts";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const userId = await getUserId(request)

  const imageUrls = await getPhotos();
  return { images: imageUrls, admin: userId }
};

export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const file = formData.get("photo");
  const intent = formData.get('intent')
  const userId = await getUserId(request)

  if (intent === "uploadPhoto") {
    if (file instanceof File) {
      await uploadPhoto(file);
      const toastHeaders = await createToastHeaders({
        title: "photo uploaded",
        description: "",
      });
      return json({ status: "success" } as const, { headers: toastHeaders });
    }
  }

  if (intent === "removePhoto" && userId) {
    const photoKey = formData.get("photoKey");
    if (photoKey) {
      await deletePhoto(photoKey as string);
      const toastHeaders = await createToastHeaders({
        title: "photo deleted",
        description: "",
      });
      return json({ status: "success" } as const, { headers: toastHeaders });
    }
  }
};

const Photos = () => {
  const fetcher = useFetcher()
  const { images, admin } = useLoaderData<typeof loader>();


  const [galleryOpen, setGalleryOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  const photoFormRef = useRef<HTMLFormElement | null>(null)


  const isAddingPhoto = fetcher.state === 'submitting' && fetcher.formData?.get('intent') === 'uploadPhoto';

  const openGallery = (index: number) => {
    setGalleryOpen(true);
    setImageIndex(index);
  };

  const closeGallery = () => {
    setGalleryOpen(false);
  };

  const showNextImage = () => {
    setImageIndex(imageIndex + 1);
  };

  const showPreviousImage = () => {
    setImageIndex(imageIndex - 1);
  };

  useEffect(() => {
    if (isAddingPhoto) {
      photoFormRef.current?.reset()
    }
  }, [fetcher.state])


  return (
    <>
      {!galleryOpen ? (
        <div className="flex flex-col p-10 space-y-8">
          <h1 className="text-3xl font-bold text-secondary">
            Photo Gallery
          </h1>

          <fetcher.Form
            ref={photoFormRef}
            method="post"
            encType="multipart/form-data"
            className="mb-10 p-8 border border-gray-200 rounded-lg shadow-lg bg-white space-y-4"
          >
            <div className="flex flex-col space-y-2">
              <label className="block text-lg font-medium text-secondary">
                Upload your photos from our wedding day!
              </label>
              <input
                type="file"
                name="photo"
                accept="image/*"
                required
                disabled={isAddingPhoto}
                className="block w-full text-sm rounded-md
                  file:mr-4 file:py-2 file:px-4
                  file:border-none file:rounded-md
                  file:bg-secondary file:text-white
                  hover:file:bg-secondary/90"
              />
            </div>
            {isAddingPhoto ?
              <p className="animate-pulse">Uploading photo...</p>
              :
              <Button
                name="intent"
                value="uploadPhoto"
                className="w-[100px] rounded-lg bg-secondary px-4 py-3 font-bold text-white hover:bg-secondary/90"
                disabled={isAddingPhoto}
              >
                Upload
              </Button>
            }
          </fetcher.Form>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {images.map((image, index) => (
              <div key={image.key} className="flex flex-col relative">
                {admin && <fetcher.Form method="post">
                  <input type="hidden" name="photoKey" value={image.key} />
                  <button
                    name="intent"
                    value="removePhoto"
                    className="absolute top-[-15px] right-0 bg-red-600 p-1 rounded-md z-10"
                  >
                    <Trash2 className="text-white w-[20px]" />
                  </button>
                </fetcher.Form>}
                <div
                  className="relative border rounded-lg h-[300px] overflow-hidden shadow-md hover:shadow-lg transform transition-all duration-300 bg-white cursor-pointer"
                  onClick={() => openGallery(index)}
                >
                  <img
                    src={image.url}
                    alt={image.key}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex justify-center items-center z-50"
          onClick={closeGallery}
        >
          <div
            className="relative w-full max-w-3xl rounded-lg shadow-lg bg-white"
            onClick={(e) => e.stopPropagation()} // Prevent clicking the image closing the gallery
          >
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              onClick={closeGallery}
            >
              <X className="w-6 h-6" />
            </button>
            <div className="w-full h-[500px] flex justify-center items-center bg-gray-100 overflow-hidden">
              <img
                src={images[imageIndex]?.url || ""}
                alt={`Image ${imageIndex + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex justify-between items-center p-4 bg-bg-main rounded-b-lg">
              <button
                onClick={showPreviousImage}
                disabled={imageIndex === 0}
                className={`p-3 rounded-full flex items-center justify-center bg-secondary text-white hover:bg-secondary/90 ${imageIndex === 0 ? "opacity-50 cursor-not-allowed" : ""
                  }`}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <p className="text-sm text-accent">{`Image ${imageIndex + 1} of ${images.length}`}</p>
              <button
                onClick={showNextImage}
                disabled={imageIndex === images.length - 1}
                className={`p-3 rounded-full flex items-center justify-center bg-secondary text-white hover:bg-secondary/90 ${imageIndex === images.length - 1
                  ? "opacity-50 cursor-not-allowed"
                  : ""
                  }`}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Photos;
